// ENG-201 (Shell P3) — runtime module configuration.
//
// The same static bundle has to behave differently per deployment: served
// same-origin by ai-assessment today, served from its own Fly app tomorrow,
// pointed at staging APIs in between. A compile-time `VITE_API_BASE_URL` cannot
// express that without rebuilding the bundle per environment, which defeats the
// point of one bundle.
//
// So the API map is read at runtime, from whichever of these is present first:
//
//   1. `window.__CLARITIO_CONFIG__`, injected inline by the host page. Cheapest —
//      no extra round trip — and how the Rails-served shell will supply it.
//   2. `/config.json`, fetched before the app renders. How the standalone static
//      app supplies it, since its index.html is a build artifact and the values
//      aren't known until the container starts.
//   3. Nothing, in which case every call goes to the origin that served the page.
//      That is exactly today's behaviour, so an app that ships no config is
//      unaffected by this file existing.

export interface ModuleConfig {
  // Route prefix this module owns, e.g. "/second-brain". Matched against the
  // *API path* being requested, not the browser URL.
  prefix: string
  // Absolute origin for that module's API, e.g. "https://sb.app.example.com".
  apiBase: string
}

export interface ClaritioConfig {
  modules?: ModuleConfig[]
  // Where anything not matching a module prefix goes. Empty = same origin.
  defaultApiBase?: string
  // OAuth Web client for "Open in Google Slides". Missing = the feature is off.
  googleClientId?: string
}

declare global {
  interface Window {
    __CLARITIO_CONFIG__?: ClaritioConfig
  }
}

// Deliberately module state rather than a React context: the API client is not a
// component and must be able to resolve a base URL synchronously, from anywhere.
let config: ClaritioConfig = {}

export function setConfig(next: ClaritioConfig | null | undefined): void {
  config = next ?? {}
}

export function getConfig(): ClaritioConfig {
  return config
}

// Call once before rendering. Safe to call when neither source exists — it
// simply leaves the same-origin default in place.
export async function loadConfig(url = '/config.json'): Promise<ClaritioConfig> {
  if (window.__CLARITIO_CONFIG__) {
    setConfig(window.__CLARITIO_CONFIG__)
    return config
  }

  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' } })
    // A 404 here is a normal, expected state (the Rails-served shell has no
    // /config.json), not an error worth surfacing — but a *malformed* config is,
    // hence the separate parse guard below.
    if (!res.ok) return config

    const parsed = (await res.json()) as ClaritioConfig
    setConfig(parsed)
  } catch {
    // Network failure or unparseable body. Falling back to same-origin is the
    // only safe move: guessing an API base would send credentialed requests
    // somewhere nobody chose.
  }

  return config
}

// Longest prefix wins, so "/api/v1/second-brain/bundles" resolves to the
// second-brain module even when a shorter prefix would also match. Ties are
// impossible — two identical prefixes are the same module.
export function apiBaseFor(path: string): string {
  const modules = config.modules ?? []
  const match = modules
    .filter((m) => path.startsWith(m.prefix))
    .sort((a, b) => b.prefix.length - a.prefix.length)[0]

  return match?.apiBase ?? config.defaultApiBase ?? ''
}
